import React from "react"
import PropTypes from "prop-types"
import { Box, Grid, Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import InfoItem from "../InfoCard/Info-item"

const resolveSx = (sx, theme) => typeof sx === "function" ? sx(theme) : sx

const InfoDataPanel = ({
    title,
    titleSx,
    data,
    loading = false,
    leftItems = [],
    sections = [],
    bottomItems = [],
}) => {
    const theme = useTheme()

    const getValue = (item) => {
        const rawValue = data?.[item.key]

        if (item.formatter) {
            return item.formatter(rawValue, data)
        }

        return rawValue ?? "-"
    }

    const getOptionalText = (item) => {
        if (item.optionalTextFormatter) {
            return item.optionalTextFormatter(data)
        }

        return item.optionalText
    }

    const getOptionalTextLabel = (item) => {
        return item.optionalTextLabel
    }

    const getLeftItemRows = () => {
        const rows = []

        for (let index = 0; index < leftItems.length; index += 2) {
            rows.push(leftItems.slice(index, index + 2))
        }

        return rows
    }

    const renderGridSection = (section, sectionIndex) => {
        return (
            <Grid
                key={`grid-section-${sectionIndex}`}
                container
                columns={section.columns}
                pt={3}
                pb={3}
                pl={3}
                sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: {
                        xxxs: "column",
                        xs: "row",
                    },
                    background: `linear-gradient(90.8deg, ${theme.palette.transparent.white10} 0%, transparent 100%)`,
                    ...resolveSx(section.sx, theme),
                }}
            >
                {section.items.map((item, itemIndex) => (
                    <Grid
                        key={item.key || `placeholder-${itemIndex}`}
                        item
                        xxxs={2}
                        md={3}
                        lg
                        sx={{
                            minWidth: 0,
                            paddingBottom: {
                                xxxs: item.placeholder ? 0 : 3,
                                md: 0,
                            },
                            display: item.placeholder
                                ? { xxxs: "none", md: "block" }
                                : "block",
                            ...item.sx,
                        }}
                        {...item.gridProps}
                    >
                        {!item.placeholder && (
                            <InfoItem
                                label={item.label}
                                value={getValue(item)}
                                loading={loading}
                                optionalText={getOptionalText(item)}
                                optionalTextLabel={getOptionalTextLabel(item)}
                                optionalTextOpacity={item.optionalTextOpacity}
                                optionalTextVariant={item.optionalTextVariant}
                            />
                        )}
                    </Grid>
                ))}
            </Grid>
        )
    }

    const renderDividedSection = (section, sectionIndex) => {
        const isFourItems = section.items.length === 4

        return (
            <Box
                key={`divided-section-${sectionIndex}`}
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: { xxxs: "wrap", md: "nowrap" },
                    width: "100%",
                    height: "100%",
                    minWidth: 0,
                    background: theme.palette.background.secondary,
                    ...resolveSx(section.sx, theme),
                }}
            >
                {section.items.map((item, itemIndex) => (
                    <Box
                        key={item.key || `divided-item-${itemIndex}`}
                        sx={{
                            flex: {
                                xxxs: isFourItems ? "0 0 50%" : "0 0 100%",
                                md: "1 1 0",
                            },
                            width: {
                                xxxs: isFourItems ? "50%" : "100%",
                                md: "auto",
                            },
                            maxWidth: {
                                xxxs: isFourItems ? "50%" : "100%",
                                md: "none",
                            },
                            minWidth: 0,
                            boxSizing: "border-box",
                            p: 3,
                            minHeight: section.itemMinHeight ?? 86,
                            display: "flex",
                            alignItems: "flex-start",
                            overflow: "hidden",
                            background: `linear-gradient(146.22deg, ${theme.palette.transparent.white10} 0%, transparent 100%)`,
                            borderLeft:
                                itemIndex === 0
                                    ? "none"
                                    : {
                                          xxxs: "none",
                                          md: "none",
                                      },
                            borderTop:
                                isFourItems && itemIndex >= 2
                                    ? {
                                          xxxs: `1px solid ${theme.palette.background.primary}`,
                                          md: "none",
                                      }
                                    : "none",
                            ...item.sx,
                        }}
                    >
                        <InfoItem
                            label={item.label}
                            value={getValue(item)}
                            optionalText={getOptionalText(item)}
                            optionalTextLabel={getOptionalTextLabel(item)}
                            optionalTextOpacity={item.optionalTextOpacity}
                            optionalTextVariant={item.optionalTextVariant}
                        />
                    </Box>
                ))}
            </Box>
        )
    }

    const renderSection = (section, sectionIndex) => {
        if (section.variant === "divided") {
            return renderDividedSection(section, sectionIndex)
        }

        return renderGridSection(section, sectionIndex)
    }

    const renderLeftItemsMobile = () => {
        return (
            <Box
                p={3}
                sx={{
                    background: `linear-gradient(146.22deg, ${theme.palette.transparent.white10} 0%, transparent 100%)`,
                }}
            >
                <Grid
                    container
                    spacing={2}
                    columns={{
                        xxxs: 4,
                        xs: 4,
                    }}
                >
                    {leftItems.map((item) => (
                        <Grid
                            key={item.key}
                            item
                            xxxs={2}
                            xs={2}
                            sx={{ minWidth: 0 }}
                            {...item.gridProps}
                        >
                            <InfoItem
                                label={item.label}
                                value={getValue(item)}
                                optionalText={getOptionalText(item)}
                                optionalTextLabel={getOptionalTextLabel(item)}
                                optionalTextOpacity={item.optionalTextOpacity}
                                optionalTextVariant={item.optionalTextVariant}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        )
    }

    const renderLeftItemsAlignedRow = (items, rowIndex) => {
        return (
            <Grid
                key={`left-items-row-${rowIndex}`}
                container
                pt={3}
                pb={3}
                px={3}
                columns={{
                    md: 12,
                    lg: 12,
                }}
                sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    zIndex: 1,
                    minWidth: 0,
                }}
            >
                {items.map((item, itemIndex) => (
                    <Grid
                        key={item.key || `left-item-${rowIndex}-${itemIndex}`}
                        item
                        md={6}
                        lg={6}
                        sx={{ minWidth: 0 }}
                        {...item.gridProps}
                    >
                        <InfoItem
                            label={item.label}
                            value={getValue(item)}
                            optionalText={getOptionalText(item)}
                            optionalTextLabel={getOptionalTextLabel(item)}
                            optionalTextOpacity={item.optionalTextOpacity}
                            optionalTextVariant={item.optionalTextVariant}
                        />
                    </Grid>
                ))}

                {items.length === 1 && <Grid item md={6} lg={6} />}
            </Grid>
        )
    }

    const leftItemRows = getLeftItemRows()
    const hasLeftItems = leftItemRows.length > 0
    const alignedRowsCount = Math.max(leftItemRows.length, sections.length)

    return (
        <Box display="flex" flexDirection="column" sx={{ minWidth: 0 }}>
            {title && (
                <Typography
                    variant="textLarge"
                    color="common.white"
                    sx={{
                        mb: 3,
                        ...titleSx,
                    }}
                >
                    {title}
                </Typography>
            )}

            {hasLeftItems ? (
                <>
                    <Box
                        sx={{
                            display: { xxxs: "flex", md: "none" },
                            flexDirection: "column",
                            gap: 0.125,
                            minWidth: 0,
                        }}
                    >
                        {renderLeftItemsMobile()}

                        {sections.map((section, sectionIndex) =>
                            renderSection(section, sectionIndex)
                        )}
                    </Box>

                    <Box
                        sx={{
                            display: { xxxs: "none", md: "grid" },
                            gridTemplateColumns: {
                                md: "260px minmax(0, 1fr)",
                                lg: "300px minmax(0, 1fr)",
                            },
                            gridAutoRows: "auto",
                            gap: 0.125,
                            alignItems: "stretch",
                            position: "relative",
                            minWidth: 0,
                        }}
                    >
                        <Box
                            sx={{
                                gridColumn: "1",
                                gridRow: `1 / span ${alignedRowsCount}`,
                                background: `linear-gradient(146.22deg, ${theme.palette.transparent.white10} 0%, transparent 100%)`,
                            }}
                        />

                        {Array.from({ length: alignedRowsCount }).map(
                            (_, rowIndex) => (
                                <React.Fragment
                                    key={`aligned-row-${rowIndex}`}
                                >
                                    <Box
                                        sx={{
                                            gridColumn: "1",
                                            gridRow: rowIndex + 1,
                                            display: "flex",
                                            height: "100%",
                                            minWidth: 0,
                                        }}
                                    >
                                        {renderLeftItemsAlignedRow(
                                            leftItemRows[rowIndex] ?? [],
                                            rowIndex
                                        )}
                                    </Box>

                                    {sections[rowIndex] && (
                                        <Box
                                            sx={{
                                                gridColumn: "2",
                                                gridRow: rowIndex + 1,
                                                display: "flex",
                                                height: "100%",
                                                minWidth: 0,
                                            }}
                                        >
                                            {renderSection(
                                                sections[rowIndex],
                                                rowIndex
                                            )}
                                        </Box>
                                    )}
                                </React.Fragment>
                            )
                        )}
                    </Box>
                </>
            ) : (
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={0.125}
                    width="100%"
                    sx={{ minWidth: 0 }}
                >
                    {sections.map((section, sectionIndex) =>
                        renderSection(section, sectionIndex)
                    )}
                </Box>
            )}

            {bottomItems.length > 0 && (
                <Grid
                    container
                    pt={3}
                    pb={3}
                    pl={3}
                    sx={{
                        display: "flex",
                        flexDirection: {
                            xxxs: "column",
                            xs: "row",
                        },
                        minWidth: 0,
                        background: `linear-gradient(90.8deg, ${theme.palette.transparent.white10} 0%, transparent 100%)`,
                    }}
                >
                    {bottomItems.map((item) => (
                        <Grid
                            key={item.key}
                            item
                            xxxs={4}
                            md={12}
                            sx={{
                                minWidth: 0,
                                ...item.sx,
                            }}
                            {...item.gridProps}
                        >
                            <InfoItem
                                label={item.label}
                                value={getValue(item)}
                                loading={loading}
                                optionalText={getOptionalText(item)}
                                optionalTextLabel={getOptionalTextLabel(item)}
                                optionalTextOpacity={item.optionalTextOpacity}
                                optionalTextVariant={item.optionalTextVariant}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    )
}

InfoDataPanel.propTypes = {
    title: PropTypes.string,
    titleSx: PropTypes.object,
    data: PropTypes.object,
    loading: PropTypes.bool,
    leftItems: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            formatter: PropTypes.func,
            optionalText: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
                PropTypes.node,
            ]),
            optionalTextFormatter: PropTypes.func,
            optionalTextLabel: PropTypes.string,
            optionalTextOpacity: PropTypes.number,
            optionalTextVariant: PropTypes.string,
            gridProps: PropTypes.object,
            sx: PropTypes.object,
        })
    ),
    sections: PropTypes.arrayOf(
        PropTypes.shape({
            variant: PropTypes.oneOf(["grid", "divided"]),
            columns: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
            sx: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
            itemMinHeight: PropTypes.number,
            items: PropTypes.arrayOf(
                PropTypes.shape({
                    key: PropTypes.string,
                    label: PropTypes.string,
                    formatter: PropTypes.func,
                    optionalText: PropTypes.oneOfType([
                        PropTypes.string,
                        PropTypes.number,
                        PropTypes.node,
                    ]),
                    optionalTextFormatter: PropTypes.func,
                    optionalTextLabel: PropTypes.string,
                    optionalTextOpacity: PropTypes.number,
                    optionalTextVariant: PropTypes.string,
                    gridProps: PropTypes.object,
                    sx: PropTypes.object,
                    placeholder: PropTypes.bool,
                })
            ).isRequired,
        })
    ),
    bottomItems: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            formatter: PropTypes.func,
            optionalText: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
                PropTypes.node,
            ]),
            optionalTextFormatter: PropTypes.func,
            optionalTextLabel: PropTypes.string,
            optionalTextOpacity: PropTypes.number,
            optionalTextVariant: PropTypes.string,
            gridProps: PropTypes.object,
            sx: PropTypes.object,
        })
    ),
}

export default InfoDataPanel
